import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const PRESET_OPTIONS = ['Today', 'Yesterday', 'This Week', 'This Month', 'This Quarter', 'Custom'];

const formatLabelRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
    return start.format('MMM DD, YYYY');
  }

  return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
};

const calculatePresetRange = (filter) => {
  const today = dayjs();
  let startDate;
  let endDate;

  switch (filter) {
    case 'Today':
      startDate = today.startOf('day').format('YYYY-MM-DD');
      endDate = today.endOf('day').format('YYYY-MM-DD');
      break;
    case 'Yesterday': {
      const yesterday = today.subtract(1, 'day');
      startDate = yesterday.startOf('day').format('YYYY-MM-DD');
      endDate = yesterday.endOf('day').format('YYYY-MM-DD');
      break;
    }
    case 'This Week':
      startDate = today.startOf('isoWeek').format('YYYY-MM-DD');
      endDate = today.endOf('day').format('YYYY-MM-DD');
      break;
    case 'This Quarter': {
      const currentQuarter = Math.floor(today.month() / 3);
      const quarterStartMonth = currentQuarter * 3;
      startDate = today.month(quarterStartMonth).startOf('month').format('YYYY-MM-DD');
      endDate = today.endOf('day').format('YYYY-MM-DD');
      break;
    }
    case 'This Month':
    default:
      startDate = today.startOf('month').format('YYYY-MM-DD');
      endDate = today.endOf('day').format('YYYY-MM-DD');
      break;
  }

  return { startDate, endDate };
};

export const getDefaultDateRange = () => ({
  filter: 'Today',
  ...calculatePresetRange('Today'),
});

/** Preset "Today" (start/end of current local day) — for reports that default to intraday scope. */
export const getTodayDateRange = () => ({
  filter: 'Today',
  ...calculatePresetRange('Today'),
});

export default function DateRangeFilter({
  value = getDefaultDateRange(),
  onChange,
  minWidth = 190,
}) {
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(() => dayjs(value.startDate));
  const [customEndDate, setCustomEndDate] = useState(() => dayjs(value.endDate));

  const dateRangeDisplay = useMemo(
    () => formatLabelRange(value.startDate, value.endDate),
    [value.startDate, value.endDate],
  );

  const handlePresetChange = (event) => {
    const selectedFilter = event.target.value;

    if (selectedFilter === 'Custom') {
      setCustomStartDate(dayjs(value.startDate));
      setCustomEndDate(dayjs(value.endDate));
      setCustomDialogOpen(true);
      return;
    }

    const newRange = calculatePresetRange(selectedFilter);
    onChange?.({
      filter: selectedFilter,
      ...newRange,
    });
  };

  const handleApplyCustom = () => {
    if (!customStartDate || !customEndDate) return;
    if (customStartDate.isAfter(customEndDate)) return;

    onChange?.({
      filter: 'Custom',
      startDate: customStartDate.format('YYYY-MM-DD'),
      endDate: customEndDate.format('YYYY-MM-DD'),
    });
    setCustomDialogOpen(false);
  };

  const getSecondaryLabel = (option) => {
    if (option === 'Custom') {
      return value.filter === 'Custom' && dateRangeDisplay ? dateRangeDisplay : 'Select date range';
    }

    const presetRange = calculatePresetRange(option);
    return formatLabelRange(presetRange.startDate, presetRange.endDate);
  };

  return (
    <>
      <FormControl size="small" sx={{ minWidth }}>
        <InputLabel sx={{ fontSize: '0.78rem' }}>Date Range</InputLabel>
        <Select
          value={value.filter}
          label="Date Range"
          onChange={handlePresetChange}
          sx={{
            height: 34,
            fontSize: '0.8rem',
            color: 'text.secondary',
            backgroundColor: 'transparent',
            '& .MuiSelect-select': { py: 0.75 },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                '& .MuiMenuItem-root': {
                  py: 0.75,
                  minHeight: 'auto',
                },
              },
            },
          }}
        >
          {PRESET_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              <Box sx={{ lineHeight: 1.1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.primary' }}>
                  {option}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.66rem' }}>
                  {getSecondaryLabel(option)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Dialog open={customDialogOpen} onClose={() => setCustomDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>Select Custom Date Range</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                maxDate={customEndDate || undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                minDate={customStartDate || undefined}
                maxDate={dayjs()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialogOpen(false)} size="small" color="inherit">
            Cancel
          </Button>
          <Button onClick={handleApplyCustom} variant="contained" size="small">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
